import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  NgZone
} from '@angular/core';
import {
  MediaPipeFaceMeshMediaPipeModelConfig,
  SupportedModels,
  createDetector,
  FaceLandmarksDetector
} from '@tensorflow-models/face-landmarks-detection';
import { CommonModule } from '@angular/common';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService, UtilsService } from '../../services';
import { CONSTANTS_TOKEN } from '../../config';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { Subscription } from 'rxjs';
import { CameraCleanupService } from '../../services/camera-cleanup.service';
import { FaceVerifyCadence } from './face-verify-cadence';

const FACE_LIVENESS_INTERVAL_MS = 10_000;
const FACE_LIVENESS_TICKS_BEFORE_VERIFY = 3;

@Component({
  selector: 'educare-face-scan',
  standalone: true,
  imports: [CommonModule, CdkDrag, TranslateModule],
  templateUrl: './face-scan.component.html',
  styleUrls: ['./face-scan.component.scss']
})
export class FaceScanComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('videoElementExpanded') videoElementExpanded!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoElementMinimized') videoElementMinimized!: ElementRef<HTMLVideoElement>;
  @ViewChild('scannerContainer') scannerContainer!: ElementRef<HTMLDivElement>;

  get videoElement(): HTMLVideoElement | null {
    const ref = this.isMinimized ? this.videoElementMinimized : this.videoElementExpanded;
    return ref?.nativeElement ?? null;
  }

  @Output() closeRequested = new EventEmitter<void>();
  @Output() screenLocked = new EventEmitter<void>();
  @Input() isVisible = false;
  @Input() isSidebarCollapsed = false;

  detector: FaceLandmarksDetector | null = null;
  intervalId: any;
  backdrop: any = null;
  isFaceDetected = true;
  isApiCallInProgress: boolean = false;
  isDetectionInProgress: boolean = false;
  isMinimized = false;
  private isAuthorized = true;
  isResizing = false;
  customWidth: number | null = null;
  customHeight: number | null = null;
  private resizeDirection: string = '';
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private readonly MIN_WIDTH = 150;
  private readonly MIN_HEIGHT = 200;
  private readonly MAX_WIDTH = 500;
  private readonly MAX_HEIGHT = 600;
  private mouseMoveListener: ((e: MouseEvent) => void) | null = null;
  private mouseUpListener: (() => void) | null = null;

  private readonly LOCK_KEY = 'FACE_SCAN_LOCK_STATE';
  private bc: BroadcastChannel | null = null;
  private tabId = `${Date.now()}_${Math.random()}`;
  private isLockedGlobally = false;

  private readonly dialog = inject(MatDialog);
  private readonly constants = inject(CONSTANTS_TOKEN);
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly overlayContainer = inject(OverlayContainer);

  /** Whether the in-flight verify HTTP call was started from the Unlock button vs liveness/cadence. */
  private activeVerifyRequestSource: 'liveness' | 'unlock' = 'liveness';
  private lockoutDialogRef: MatDialogRef<ConfirmationDialogComponent> | null = null;
  private isLockoutDialogOpen = false;
  private screenLockDomObserver: MutationObserver | null = null;
  private isReattachingScreenLock = false;
  private mediaStream: MediaStream | null = null;
  private readonly FACE_CHECK_INTERVAL = 3000;

  private livenessCount = 0;
  private lastLivenessTickAt = 0;

  private subscriptions = new Subscription();
  private isDestroyed = false;

  private readonly verifyCadence = new FaceVerifyCadence(this.ngZone, {
    isDestroyed: () => this.isDestroyed,
    isScannerVisible: () => this.isVisible,
    isVerifyInFlight: () => this.isApiCallInProgress,
    isVideoFrameReady: () => this.isVideoReadyForCapture(),
    fireScheduledVerify: () => this.captureAndSendPhoto()
  });

  constructor(
    private authService: AuthService,
    private utils: UtilsService,
    private cameraCleanup: CameraCleanupService
  ) {}

  async ngOnInit() {
    this.initCrossTabSync();
    if (this.isVisible) {
      await this.initializeScanner();
    }
  }

  private initCrossTabSync(): void {
    if ('BroadcastChannel' in window) {
      this.bc = new BroadcastChannel('face-scan-lock');
      this.bc.onmessage = (ev) => this.handleLockMessage(ev.data);
    }
    window.addEventListener('storage', this.onStorageEvent);
    const raw = localStorage.getItem(this.LOCK_KEY);
    if (raw) {
      try {
        this.handleLockMessage(JSON.parse(raw));
      } catch {
        /* empty */
      }
    }
  }

  private destroyCrossTabSync(): void {
    try {
      this.bc?.close();
    } catch {
      /* empty */
    }
    this.bc = null;
    window.removeEventListener('storage', this.onStorageEvent);
  }

  private onStorageEvent = (e: StorageEvent) => {
    if (e.key !== this.LOCK_KEY || !e.newValue) return;
    try {
      this.handleLockMessage(JSON.parse(e.newValue));
    } catch {
      /* empty */
    }
  };

  private broadcastLockState(locked: boolean): void {
    const payload = { locked, ts: Date.now(), from: this.tabId };
    const json = JSON.stringify(payload);
    for (const fn of [() => this.bc?.postMessage(payload), () => localStorage.setItem(this.LOCK_KEY, json)]) {
      try {
        fn();
      } catch {
        /* empty */
      }
    }
  }

  private handleLockMessage(msg: any): void {
    if (!msg || typeof msg.locked !== 'boolean') return;
    this.isLockedGlobally = msg.locked;
    if (this.isLockedGlobally) {
      this.openFaceNotDetectedDialog();
    } else {
      this.closeFaceNotDetectedDialog();
    }
  }

  private resetLivenessProgress(): void {
    this.livenessCount = 0;
    this.lastLivenessTickAt = 0;
  }

  private lockScreen(): void {
    this.resetLivenessProgress();
    this.isFaceDetected = false;
    this.isAuthorized = false;
    this.setBackdrop(true);
    this.broadcastLockState(true);
    this.openFaceNotDetectedDialog();
    this.screenLocked.emit();
  }

  private unlockScreen(): void {
    this.isFaceDetected = true;
    this.isAuthorized = true;
    this.setBackdrop(false);
    this.broadcastLockState(false);
    this.closeFaceNotDetectedDialog();
  }

  private onUnlockButtonClicked(): void {
    void this.captureAndSendPhoto({ source: 'unlock' });
  }

  async initializeScanner() {
    const userInfo = this.authService.getUserInfo();
    if (!userInfo) return;
    this.backdrop = document.querySelector(`.face-scan-backdrop`);
    this.setBackdrop(false);
    this.isApiCallInProgress = false;
    try {
      await this.setupCamera();
      await this.loadModel();
      this.startDetection();
    } catch (error) {
      this.isApiCallInProgress = false;
    }
  }

  async setupCamera() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
    if (this.isDestroyed) {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error('Component destroyed during camera setup');
    }

    this.cameraCleanup.registerStream(stream);
    this.mediaStream = stream;
    this.videoElement!.srcObject = stream;

    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.onended = () => {
        if (!this.isDestroyed) this.isFaceDetected = false;
      };
    }
    return new Promise<void>((resolve, reject) => {
      this.videoElement!.onloadedmetadata = () => {
        if (this.isDestroyed) {
          stream.getTracks().forEach((track) => track.stop());
          reject(new Error('Component destroyed during camera setup'));
          return;
        }

        this.videoElement!.play();

        const checkReady = () => {
          const video = this.videoElement!;
          if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      };
    });
  }

  async loadModel() {
    try {
      if (this.detector) {
        try {
          this.detector.dispose();
        } catch (e: any) {
          console.warn('[FaceScan] Error disposing old detector:', e);
        }
        this.detector = null;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (typeof window !== 'undefined') {
        const checkMediaPipe = () => {
          return (
            typeof (window as any).MediaPipeFaceMesh !== 'undefined' ||
            document.querySelector('script[src*="mediapipe"]') !== null
          );
        };

        if (!checkMediaPipe()) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
      const modelConfig: MediaPipeFaceMeshMediaPipeModelConfig = {
        runtime: 'mediapipe',
        refineLandmarks: false,
        solutionPath: `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh`
      };

      this.detector = await createDetector(SupportedModels.MediaPipeFaceMesh, modelConfig);
      if (!this.detector) throw new Error('Detector created but is null');
    } catch (error: any) {
      this.detector = null;

      const msg = error?.message ?? '';
      const retryable = msg.includes('buffer') || msg.includes('undefined') || msg.includes('Cannot read properties');
      if (retryable) {
        console.warn('[FaceScan] Buffer/undefined error detected, will retry model loading in 5 seconds...');
        setTimeout(async () => {
          if (this.isVisible && !this.detector) {
            try {
              await this.loadModel();
              if (this.detector) this.startDetection();
            } catch (retryError: any) {
              console.error('[FaceScan] Retry failed:', retryError);
            }
          }
        }, 5000);
      }
      if (!msg.includes('buffer') && !msg.includes('undefined')) {
        throw error;
      }
    }
  }

  startDetection() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    setTimeout(() => {
      this.detect();
      this.intervalId = setInterval(() => this.detect(), this.FACE_CHECK_INTERVAL);
    }, 500);
  }

  async detect() {
    if (this.isDetectionInProgress) return;

    if (!this.detector) {
      if (this.isVisible) {
        try {
          await this.loadModel();
        } catch (error) {
          console.error('[FaceScan] Failed to reload model:', error);
        }
      }
      return;
    }

    if (!this.videoElement) {
      console.warn('[FaceScan] Video element not ready');
      return;
    }
    const video = this.videoElement;
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      console.warn('[FaceScan] Video not ready');
      return;
    }
    this.isDetectionInProgress = true;

    try {
      const faces = await this.detector.estimateFaces(video);
      if (faces.length === 1) {
        if (this.isAuthorized) {
          this.broadcastLockState(false);
          this.isFaceDetected = true;
          this.closeFaceNotDetectedDialog();
          this.setBackdrop(false);
          const now = Date.now();
          if (this.lastLivenessTickAt === 0) {
            this.lastLivenessTickAt = now;
          } else if (now - this.lastLivenessTickAt >= FACE_LIVENESS_INTERVAL_MS) {
            this.lastLivenessTickAt = now;
            this.livenessCount++;
          }
          if (this.livenessCount >= FACE_LIVENESS_TICKS_BEFORE_VERIFY && !this.isApiCallInProgress) {
            this.livenessCount = 0;
            this.lastLivenessTickAt = now;
            this.captureAndSendPhoto();
          }
        } else {
          this.broadcastLockState(true);
          this.isFaceDetected = false;
          this.openFaceNotDetectedDialog();
          this.setBackdrop(true);
        }
      } else {
        if (this.verifyCadence.awaitingFirstResponse && !this.isApiCallInProgress) {
          this.captureAndSendPhoto();
        } else if (!this.verifyCadence.awaitingFirstResponse && !this.isApiCallInProgress) {
          this.verifyCadence.notifyEnvironmentalLockout();
        }
        this.lockScreen();
        return;
      }
    } catch (error: any) {
      if (
        error?.message?.includes('abort') ||
        error?.message?.includes('RuntimeError') ||
        error?.stack?.includes('abort')
      ) {
        console.warn('[FaceScan] WASM abort error detected, will reload model on next cycle');
        try {
          if (this.detector) {
            this.detector.dispose();
            this.detector = null;
          }
        } catch (disposeError) {
          console.warn('[FaceScan] Error disposing detector:', disposeError);
        }
        setTimeout(async () => {
          if (this.isVisible && !this.detector) {
            try {
              await this.loadModel();
            } catch (reloadError) {
              console.error('[FaceScan] Failed to reload model:', reloadError);
            }
          }
        }, 3000);
      }
    } finally {
      this.isDetectionInProgress = false;
    }
  }

  private setBackdrop(visible: boolean): void {
    if (this.backdrop) this.backdrop.style.display = visible ? 'block' : 'none';
  }

  private ensureOverlayContainerInDom(): void {
    const container = document.body.querySelector('.cdk-overlay-container');
    if (!container) {
      const oc = this.overlayContainer as unknown as { _containerElement: HTMLElement | null };
      if (oc._containerElement != null) oc._containerElement = null;
    }
  }

  private openFaceNotDetectedDialog(): void {
    if (this.isLockoutDialogOpen) return;
    this.ensureOverlayContainerInDom();
    this.isLockoutDialogOpen = true;
    this.lockoutDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      width: '485px',
      maxWidth: '485px',
      panelClass: 'face-not-detected-dialog',
      data: {
        title: this.translate.instant('faceScan.screenLock'),
        description: this.translate.instant('faceScan.faceNotRecognised'),
        submitButtonText: this.translate.instant('faceScan.unlock'),
        cancelButtonText: null,
        allowHtml: false,
        submitWithoutClose: true,
        onSubmitClick: () => this.onUnlockButtonClicked(),
        isSubmitDisabled: () => this.isApiCallInProgress
      }
    });

    this.observeScreenLockDom();

    const dialogSub = this.lockoutDialogRef.afterClosed().subscribe(() => {
      if (this.isDestroyed) return;
      this.isLockoutDialogOpen = false;
      this.lockoutDialogRef = null;
      this.stopObserveScreenLockDom();
    });

    this.subscriptions.add(dialogSub);
  }

  private refreshScreenLockSubmitState(): void {
    this.lockoutDialogRef?.componentInstance?.notifySubmitStateChange();
  }

  private closeFaceNotDetectedDialog(): void {
    if (!this.isLockoutDialogOpen) return;
    this.stopObserveScreenLockDom();
    this.lockoutDialogRef?.close();
    this.isLockoutDialogOpen = false;
    this.lockoutDialogRef = null;
  }

  private observeScreenLockDom(): void {
    this.stopObserveScreenLockDom();
    this.ngZone.runOutsideAngular(() => {
      this.screenLockDomObserver = new MutationObserver((mutations) => {
        const isScreenLockGone = () => !document.body.querySelector('.face-not-detected-dialog');

        const removedOverlayOrDialog = Array.from(mutations).some((mutation) =>
          Array.from(mutation.removedNodes).some((node) => {
            if (!(node instanceof HTMLElement)) return false;
            return (
              node.classList?.contains('cdk-overlay-container') ||
              node.classList?.contains('cdk-global-overlay-wrapper') ||
              node.classList?.contains('cdk-overlay-pane') ||
              node.classList?.contains('face-not-detected-dialog') ||
              node.querySelector?.('.face-not-detected-dialog') != null
            );
          })
        );
        const shouldReattach =
          (this.isLockedGlobally || !this.isAuthorized) &&
          !this.isReattachingScreenLock &&
          !this.isDestroyed &&
          (removedOverlayOrDialog || (this.isLockoutDialogOpen && isScreenLockGone()));

        if (shouldReattach) {
          this.isReattachingScreenLock = true;
          this.lockoutDialogRef = null;
          this.isLockoutDialogOpen = false;
          this.ngZone.run(() => {
            setTimeout(() => {
              if (!this.isDestroyed && (this.isLockedGlobally || !this.isAuthorized)) {
                this.openFaceNotDetectedDialog();
              }
              this.isReattachingScreenLock = false;
            }, 1000);
          });
        }
      });
      this.screenLockDomObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  private stopObserveScreenLockDom(): void {
    this.screenLockDomObserver?.disconnect();
    this.screenLockDomObserver = null;
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    this.resetCustomSize();
    setTimeout(() => this.syncVideoStream(), 50);
  }

  private syncVideoStream(): void {
    const [source, target] = this.isMinimized
      ? [this.videoElementExpanded?.nativeElement, this.videoElementMinimized?.nativeElement]
      : [this.videoElementMinimized?.nativeElement, this.videoElementExpanded?.nativeElement];
    if (source?.srcObject && target) {
      target.srcObject = source.srcObject;
      target.play().catch(() => {});
    }
  }

  startResize(event: MouseEvent, direction: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.resizeDirection = direction;
    this.startX = event.clientX;
    this.startY = event.clientY;
    const container = this.scannerContainer?.nativeElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      this.startWidth = rect.width;
      this.startHeight = rect.height;
      if (this.customWidth === null) this.customWidth = rect.width;
      if (this.customHeight === null) this.customHeight = rect.height;
    }
    this.mouseMoveListener = (e: MouseEvent) => this.onResize(e);
    this.mouseUpListener = () => this.stopResize();
    document.addEventListener('mousemove', this.mouseMoveListener);
    document.addEventListener('mouseup', this.mouseUpListener);
  }

  private onResize(event: MouseEvent): void {
    if (!this.isResizing) return;
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    let newWidth = this.startWidth;
    let newHeight = this.startHeight;
    if (this.resizeDirection.includes('e')) newWidth = this.startWidth + deltaX;
    if (this.resizeDirection.includes('w')) newWidth = this.startWidth - deltaX;
    if (this.resizeDirection.includes('s')) newHeight = this.startHeight + deltaY;
    if (this.resizeDirection.includes('n')) newHeight = this.startHeight - deltaY;
    newWidth = Math.max(this.MIN_WIDTH, Math.min(this.MAX_WIDTH, newWidth));
    newHeight = Math.max(this.MIN_HEIGHT, Math.min(this.MAX_HEIGHT, newHeight));
    this.customWidth = newWidth;
    this.customHeight = newHeight;
  }

  private stopResize(): void {
    this.isResizing = false;
    this.resizeDirection = '';
    if (this.mouseMoveListener) {
      document.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = null;
    }
    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener);
      this.mouseUpListener = null;
    }
  }

  resetCustomSize(): void {
    this.customWidth = null;
    this.customHeight = null;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['isSidebarCollapsed'] && !changes['isSidebarCollapsed'].firstChange) {
      const collapsed = this.isSidebarCollapsed;
      if ((collapsed && !this.isMinimized) || (!collapsed && this.isMinimized)) {
        this.isMinimized = collapsed;
        setTimeout(() => this.syncVideoStream(), 50);
      }
    }

    if (this.isVisible) {
      const userInfo = this.authService.getUserInfo();
      if (!userInfo) return;
      if (!this.detector) {
        await this.initializeScanner();
      } else {
        if (!this.intervalId) this.startDetection();
      }
      if (changes['isVisible'] && !changes['isVisible'].firstChange && changes['isVisible'].currentValue === true) {
        this.verifyCadence.onScannerShownAgain();
      }
    } else {
      this.closeFaceNotDetectedDialog();
      this.verifyCadence.clearSchedule();
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => {
          track.onended = null;
          track.stop();
        });
        this.mediaStream = null;
      }
      if (this.videoElement?.srcObject) {
        this.videoElement.srcObject = null;
      }
    }
  }

  private isVideoReadyForCapture(): boolean {
    const video = this.videoElement;
    return !!video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
  }

  async captureAndSendPhoto(options?: { source?: 'liveness' | 'unlock' }) {
    if (this.isApiCallInProgress) return;
    if (!this.videoElement) {
      console.warn('[FaceScan] Camera not ready');
      return;
    }
    this.activeVerifyRequestSource = options?.source ?? 'liveness';
    this.isApiCallInProgress = true;
    this.refreshScreenLockSubmitState();
    this.performFaceVerification().catch((error) => {
      console.error('[FaceScan] Error in face verification:', error);
      this.isApiCallInProgress = false;
      this.refreshScreenLockSubmitState();
    });
  }

  private settleVerifyResponse(matched: boolean, failure?: { kind: 'http' | 'client'; err: unknown }): void {
    if (this.isDestroyed) return;
    const verifySource = this.activeVerifyRequestSource;
    this.isApiCallInProgress = false;
    this.refreshScreenLockSubmitState();
    if (matched) {
      this.resetLivenessProgress();
      this.verifyCadence.notifyVerifyResponse(true);
      this.unlockScreen();
      return;
    }
    this.verifyCadence.notifyVerifyResponse(false);
    if (failure?.kind === 'http') console.error('[FaceScan] API Error:', failure.err);
    else if (failure?.kind === 'client') console.error('[FaceScan] Error in face verification:', failure.err);
    if (verifySource === 'unlock') {
      return;
    }
    this.lockScreen();
  }

  private async performFaceVerification() {
    try {
      if (!this.isVideoReadyForCapture()) {
        this.isApiCallInProgress = false;
        this.refreshScreenLockSubmitState();
        return;
      }
      const video = this.videoElement!;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        this.isApiCallInProgress = false;
        this.refreshScreenLockSubmitState();
        return;
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/jpeg', 1));
      const formData = new FormData();
      formData.append('face', blob, `face_${Date.now()}.jpg`);
      const faceVerifyEndpoint = this.utils.getEncryptedUrls(this.constants.api.privateEndpoints.faceVerify);
      const subscription = this.authService.post(faceVerifyEndpoint, formData).subscribe({
        next: (response: any) => this.settleVerifyResponse(!!response.data?.matched),
        error: (err) => this.settleVerifyResponse(false, { kind: 'http', err })
      });
      this.subscriptions.add(subscription);
    } catch (err) {
      this.settleVerifyResponse(false, { kind: 'client', err });
    }
  }

  ngOnDestroy(): void {
    this.destroyCrossTabSync();
    this.isDestroyed = true;
    this.verifyCadence.clearSchedule();
    this.stopObserveScreenLockDom();
    this.subscriptions.unsubscribe();
    this.closeFaceNotDetectedDialog();
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.stopResize();
    if (this.mediaStream) {
      const tracks = this.mediaStream.getTracks();
      tracks.forEach((track) => {
        track.onended = null;
        track.stop();
      });
      this.cameraCleanup.unregisterStream(this.mediaStream);
      this.mediaStream = null;
    }

    if (this.videoElement?.srcObject) {
      this.videoElement.srcObject = null;
    }
    if (this.detector) {
      try {
        this.detector.dispose();
      } catch (error: any) {
        console.warn('[FaceScan] Error disposing detector:', error);
      }
      this.detector = null;
    }
    this.isApiCallInProgress = false;
  }
}
